import React from 'react';
import { BarChart, Trash2, Layers } from 'lucide-react';
import { formatNumber } from '../../../../../utils/formatting';
import Button from '../../../../../components/ui/Button';

/**
 * Компонент боковой панели для сохраненных расчетов бассейнов
 */
const SavedCalculationsSidebar = ({ 
  savedCalculations, 
  deleteSavedCalculation, 
  loadSavedCalculation 
}) => {
  // Если нет сохраненных расчетов
  if (!savedCalculations || savedCalculations.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
          <BarChart className="w-5 h-5 mr-2 text-green-500" />
          Сохраненные расчеты
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Здесь будут отображаться сохраненные расчеты бассейнов для сравнения.
        </p>
      </div>
    );
  }

  // Форматирование отображаемого имени типа бассейна
  const formatPoolType = (type) => {
    switch (type) {
      case 'rectangular': return 'прямоугольный';
      case 'circular': return 'круглый';
      case 'oval': return 'овальный';
      case 'irregular': return 'сложной формы';
      case 'custom': return 'произвольной формы';
      default: return type;
    }
  };

  // Форматирование даты в удобочитаемом виде
  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' };
    return new Date(dateString).toLocaleDateString('ru-RU', options);
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
          <BarChart className="w-5 h-5 mr-2 text-green-500" />
          Сохраненные расчеты
        </h3>
        <span className="text-sm text-gray-500 dark:text-gray-400">
          {savedCalculations.length} {savedCalculations.length === 1 ? 'расчет' : savedCalculations.length < 5 ? 'расчета' : 'расчетов'}
        </span>
      </div>

      <div className="space-y-4">
        {savedCalculations.map((calculation) => (
          <div 
            key={calculation.id} 
            className="bg-gray-50 dark:bg-gray-700/30 border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors group"
          >
            <div className="flex justify-between items-start mb-2">
              <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                {calculation.name}
              </h4>
              <div className="flex items-center space-x-2">
                <Button
                  size="sm"
                  variant="ghost"
                  icon={<Layers className="w-4 h-4" />}
                  onClick={() => loadSavedCalculation(calculation.id)}
                  title="Загрузить параметры"
                />
                <Button
                  size="sm"
                  variant="ghost"
                  icon={<Trash2 className="w-4 h-4 text-red-500" />}
                  onClick={() => deleteSavedCalculation(calculation.id)}
                  title="Удалить"
                />
              </div>
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">
              <div className="flex justify-between mb-1">
                <span>Объем воды:</span>
                <span className="font-medium text-green-600 dark:text-green-400">
                  {formatNumber(calculation.results.netVolume, 2)} м³
                </span>
              </div>
              <div className="flex justify-between mb-1">
                <span>Тип бассейна:</span>
                <span>
                  {formatPoolType(calculation.formData.poolType)}
                </span>
              </div>
              <div className="flex justify-between mb-1">
                <span>Размеры:</span>
                <span>
                  {calculation.formData.poolType === 'rectangular' || calculation.formData.poolType === 'oval' 
                    ? `${calculation.formData.length}×${calculation.formData.width}×${calculation.formData.depth}` 
                    : calculation.formData.poolType === 'circular' 
                      ? `Ø${calculation.formData.diameter}×${calculation.formData.depth}` 
                      : `${calculation.formData.depth}м глубина`}
                </span>
              </div>
              <div className="flex justify-between">
                <span>Сохранено:</span>
                <span className="text-gray-500 dark:text-gray-400 text-xs">
                  {formatDate(calculation.date)}
                </span>
              </div>
            </div>
            
            {/* Дополнительные детали при наведении */}
            <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-700 hidden group-hover:block">
              <div className="grid grid-cols-2 gap-x-2 gap-y-1 text-xs">
                <div className="text-gray-500 dark:text-gray-400">Площадь поверхности:</div>
                <div className="text-right text-gray-700 dark:text-gray-300">{formatNumber(calculation.results.surfaceArea, 2)} м²</div>
                
                <div className="text-gray-500 dark:text-gray-400">Система очистки:</div>
                <div className="text-right text-gray-700 dark:text-gray-300">
                  {calculation.formData.waterType === 'chlorine' ? 'Хлор' :
                   calculation.formData.waterType === 'salt' ? 'Соляная' :
                   calculation.formData.waterType === 'bromine' ? 'Бром' :
                   calculation.formData.waterType === 'ozone' ? 'Озонирование' :
                   calculation.formData.waterType === 'uv' ? 'УФ-очистка' : 'Стандартная'}
                </div>
                
                <div className="text-gray-500 dark:text-gray-400">Расход воды в год:</div>
                <div className="text-right text-gray-700 dark:text-gray-300">{formatNumber(calculation.results.waterConsumptionAnnual, 1)} м³</div>
              </div>
              
              <button
                onClick={() => loadSavedCalculation(calculation.id)}
                className="mt-2 w-full text-xs py-1 text-center text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 transition-colors"
              >
                Загрузить и применить
              </button>
            </div>
          </div>
        ))}
      </div>
      
      <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
        <p className="text-xs text-gray-500 dark:text-gray-400">
          Сохраненные расчеты помогают сравнивать параметры разных бассейнов и подбирать оптимальную конфигурацию
        </p>
      </div>
    </div>
  );
};

export default SavedCalculationsSidebar;